import { computed, getCurrentScope, onScopeDispose, ref } from 'vue';

const IDLE_AFTER_MS = 60_000;

const INPUT_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

/**
 * Чи секретар ЗАРАЗ дивиться на це вікно — а не лише чи воно відкрите.
 *
 * `visibilityState === 'visible'` цього не каже: вкладка лишається «видимою»,
 * коли поверх неї відкрита інша програма або вікно стоїть на другому моніторі.
 * Саме так секретарі й пропускали нові повідомлення: чат відкритий, heartbeat
 * кожні 10 с позначав його прочитаним, звук і сповіщення мовчали, бо «чат же
 * відкритий», — а людина сиділа в Word.
 *
 * Тому увага — це три умови разом: вкладка видима, вікно у фокусі і була
 * хоч якась дія (миша, клавіатура, колесо, дотик) за останню хвилину. Остання
 * умова ловить вікно, яке у фокусі, але перед яким нікого немає.
 *
 * Стан один на весь застосунок: слухачі вішаються при першому споживачі й
 * знімаються, коли зникає останній.
 */
const visible = ref(true);
const focused = ref(true);
const recentInput = ref(true);

let lastInputAt = Date.now();
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let consumers = 0;

const scheduleIdleCheck = () => {
  if (idleTimer !== null) {
    return;
  }

  // Таймер не переставляється на кожен рух миші: коли він спрацює, сам
  // дорахує, скільки лишилось від останньої дії, і за потреби заведеться знову.
  idleTimer = setTimeout(() => {
    idleTimer = null;
    const remaining = lastInputAt + IDLE_AFTER_MS - Date.now();

    if (remaining <= 0) {
      recentInput.value = false;

      return;
    }

    scheduleIdleCheck();
  }, Math.max(lastInputAt + IDLE_AFTER_MS - Date.now(), 0));
};

const onInput = () => {
  lastInputAt = Date.now();
  if (!recentInput.value) {
    recentInput.value = true;
  }
  scheduleIdleCheck();
};

const syncWindowState = () => {
  visible.value = document.visibilityState === 'visible';
  focused.value = document.hasFocus();
};

const attach = () => {
  syncWindowState();
  // Щойно відкрита сторінка — це вже дія: людина саме на неї перейшла.
  onInput();

  document.addEventListener('visibilitychange', syncWindowState);
  window.addEventListener('focus', syncWindowState);
  window.addEventListener('blur', syncWindowState);
  for (const type of INPUT_EVENTS) {
    window.addEventListener(type, onInput, { passive: true, capture: true });
  }
};

const detach = () => {
  document.removeEventListener('visibilitychange', syncWindowState);
  window.removeEventListener('focus', syncWindowState);
  window.removeEventListener('blur', syncWindowState);
  for (const type of INPUT_EVENTS) {
    window.removeEventListener(type, onInput, { capture: true });
  }
  if (idleTimer !== null) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
};

const isFocused = computed(() => visible.value && focused.value);
const isAttending = computed(() => isFocused.value && recentInput.value);

export const useWindowAttention = () => {
  if (typeof window !== 'undefined' && getCurrentScope()) {
    if (consumers === 0) {
      attach();
    }
    consumers += 1;

    onScopeDispose(() => {
      consumers -= 1;
      if (consumers === 0) {
        detach();
      }
    });
  }

  return {
    /** Вкладка видима і вікно у фокусі — без огляду на дії. */
    isFocused,
    /** Вікно у фокусі і людина щось робила за останню хвилину. */
    isAttending,
  };
};
